<template>
	<V2Layout>
		<div class="v2-req">

			<!-- Header -->
			<div class="v2-req__header">
				<div class="v2-req__header-left">
					<svg class="v2-req__header-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/>
						<path d="M8 10L11 13L16 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					<h1 class="v2-req__title">Requests</h1>
					<span class="v2-req__count">{{ totalCount }} total</span>
				</div>
				<div class="v2-req__header-right">
					<router-link
						v-if="isDSP"
						:to="{ name: 'v2-request-add' }"
						class="v2-req__add-btn"
					>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
						New Request
					</router-link>
				</div>
			</div>

			<!-- Filter Bar -->
			<div class="v2-req__filters">
				<div class="v2-req__pills">
					<button
						v-for="f in statusFilters"
						:key="f.value"
						class="v2-req__pill"
						:class="{ 'v2-req__pill--active': activeStatus === f.value }"
						@click="setStatus(f.value)"
					>
						{{ f.label }}
						<span v-if="f.count != null" class="v2-req__pill-count">{{ f.count }}</span>
					</button>
				</div>

				<div class="v2-req__filter-right">
					<div class="v2-req__search">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.3"/><path d="M9.5 9.5L13 13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search requests..."
							class="v2-req__search-input"
							@input="onSearch"
						/>
					</div>

					<div class="v2-req__view-toggle">
						<button
							class="v2-req__view-btn"
							:class="{ 'v2-req__view-btn--active': viewMode === 'cards' }"
							@click="viewMode = 'cards'"
							title="Card view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
						</button>
						<button
							class="v2-req__view-btn"
							:class="{ 'v2-req__view-btn--active': viewMode === 'table' }"
							@click="viewMode = 'table'"
							title="Table view"
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4H14M2 8H14M2 12H14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
						</button>
					</div>
				</div>
			</div>

			<!-- Card View -->
			<div v-if="viewMode === 'cards'" class="v2-req__cards">
				<div
					v-for="req in requests"
					:key="req.id"
					class="v2-req-card"
					@click="goToRequest(req)"
				>
					<div class="v2-req-card__top">
						<div class="v2-req-card__name-block">
							<span class="v2-req-card__name">{{ req.name }}</span>
						</div>
						<span class="v2-req-card__status" :class="statusBadgeClass(req)">{{ statusLabel(req) }}</span>
					</div>

					<div class="v2-req-card__info">
						<div class="v2-req-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1V11M3 4L6 1L9 4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>
							<span v-html="formatCash(req.budget, req.currency)"></span>
						</div>
						<div class="v2-req-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="2" y="3" width="8" height="6" rx="1" stroke="currentColor" stroke-width="1.1"/><path d="M4 6H8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							{{ req.device_count }} devices
						</div>
						<div class="v2-req-card__row">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1.5" y="2" width="9" height="8" rx="1" stroke="currentColor" stroke-width="1.1"/><path d="M1.5 4.5H10.5M4 1V3M8 1V3" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>
							{{ formatDate(req.start_date) }} &mdash; {{ formatDate(req.end_date) }}
						</div>
					</div>

					<div class="v2-req-card__footer">
						<span v-if="isSSP" class="v2-req-card__agency">{{ req.owner_instance ? req.owner_instance.name : '' }}</span>
						<span v-else class="v2-req-card__agency"></span>
						<span class="v2-req-card__date">{{ formatDateTime(req.ctime) }}</span>
					</div>
				</div>

				<div v-if="!requests.length" class="v2-req__empty">
					<p>No requests found</p>
					<p class="v2-req__empty-sub">Try adjusting your search or filters</p>
				</div>
			</div>

			<!-- Table View -->
			<div v-else class="v2-req__table-wrap">
				<table class="nice-table">
					<thead>
						<tr>
							<th>ID&nbsp;&mdash;</th>
							<th>Name&nbsp;&mdash;</th>
							<th v-if="isSSP">DSP Agency&nbsp;&mdash;</th>
							<th>Payment&nbsp;Model&nbsp;&mdash;</th>
							<th>Status&nbsp;&mdash;</th>
							<th>Budget&nbsp;&mdash;</th>
							<th>Devices&nbsp;&mdash;</th>
							<th>Start&nbsp;&mdash;</th>
							<th>End&nbsp;&mdash;</th>
							<th>Created&nbsp;&mdash;</th>
							<th>Updated&nbsp;&mdash;</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="req in requests"
							:key="req.id"
							class="v2-req__row"
							@click="goToRequest(req)"
						>
							<td>{{ req.id }}</td>
							<td class="v2-req__row-name">{{ req.name }}</td>
							<td v-if="isSSP">{{ req.owner_instance ? req.owner_instance.name : '' }}</td>
							<td>{{ req.payment_model }}</td>
							<td><span class="v2-req-card__status" :class="statusBadgeClass(req)">{{ statusLabel(req) }}</span></td>
							<td v-html="formatCash(req.budget, req.currency)"></td>
							<td>{{ req.device_count }}</td>
							<td>{{ formatDate(req.start_date) }}</td>
							<td>{{ formatDate(req.end_date) }}</td>
							<td>{{ formatDateTime(req.ctime) }}</td>
							<td>{{ formatDateTime(req.mtime) }}</td>
						</tr>
						<tr v-if="!requests.length"><td :colspan="isSSP ? 10 : 9" class="v2-req__empty-cell">No requests found</td></tr>
					</tbody>
				</table>
			</div>

			<!-- Pagination -->
			<div class="v2-req__pagination">
				<span class="v2-req__pagination-info">Showing {{ paginationFrom }}&ndash;{{ paginationTo }} of {{ totalCount }}</span>
				<div class="v2-req__pagination-right">
					<span class="v2-req__pagination-label">Lines per page</span>
					<select v-model.number="pageSize" class="v2-req__pagination-select" @change="loadPage(1)">
						<option :value="20">20</option>
						<option :value="50">50</option>
						<option :value="100">100</option>
					</select>
					<div class="v2-req__pagination-pages">
						<button class="v2-req__page-btn" :disabled="page <= 1" @click="loadPage(page - 1)">&lt;</button>
						<button v-for="p in visiblePages" :key="p" class="v2-req__page-btn" :class="{ 'v2-req__page-btn--active': p === page }" @click="loadPage(p)">{{ p }}</button>
						<button class="v2-req__page-btn" :disabled="page >= totalPages" @click="loadPage(page + 1)">&gt;</button>
					</div>
				</div>
			</div>

			<router-view />
		</div>
	</V2Layout>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import V2Layout from '@/layouts/V2Layout';
import {
	formatCash,
	profileFormatDate as formatDate,
	profileFormatDateTime as formatDateTime,
} from '@/utilites';
import {
	DRAFT as STATUS_DRAFT,
	PENDING_AUDIT as STATUS_PENDING_AUDIT,
	PRE_APPROVED as STATUS_PRE_APPROVED,
	APPROVED as STATUS_APPROVED,
	DENIED as STATUS_DENIED,
	ARCHIVED as STATUS_ARCHIVED,
} from '@/constants/audit-status-codes';

const STATUS_LABEL_MAP = {
	[STATUS_DRAFT]: { label: 'Draft', modifier: 'gray' },
	[STATUS_PENDING_AUDIT]: { label: 'Awaiting', modifier: 'yellow' },
	[STATUS_PRE_APPROVED]: { label: 'Pre-approved', modifier: 'blue' },
	[STATUS_APPROVED]: { label: 'Approved', modifier: 'green' },
	[STATUS_DENIED]: { label: 'Rejected', modifier: 'red' },
	[STATUS_ARCHIVED]: { label: 'Archived', modifier: 'gray' },
};

export default {
	name: 'V2RequestList',
	components: { V2Layout },

	data() {
		return {
			requests: [],
			totalCount: 0,
			page: 1,
			pageSize: 20,
			searchQuery: '',
			activeStatus: '---',
			viewMode: localStorage.getItem('v2RequestViewMode') || 'cards',
			searchTimeout: null,
		};
	},

	async mounted() {
		await this.fetchRequests();
	},

	watch: {
		viewMode(val) { localStorage.setItem('v2RequestViewMode', val); },
		appAgency(value, oldValue) {
			if (value && value !== oldValue) {
				this.fetchRequests();
			}
		},
	},

	computed: {
		...mapState('app', { appSide: 'side', appAgency: 'agency' }),
		...mapGetters('app', ['isDSP', 'isSSPViewer']),

		isSSP() {
			return this.appSide === 'ssp';
		},

		statusFilters() {
			const filters = [
				{ value: '---', label: 'All', count: this.totalCount },
			];
			if (this.isDSP) {
				filters.push({ value: STATUS_DRAFT, label: 'Draft', count: null });
			}
			filters.push(
				{ value: STATUS_PENDING_AUDIT, label: 'Awaiting', count: null },
				{ value: STATUS_PRE_APPROVED, label: 'Pre-approved', count: null },
				{ value: STATUS_APPROVED, label: 'Approved', count: null },
				{ value: STATUS_DENIED, label: 'Rejected', count: null },
			);
			if (this.isDSP) {
				filters.push({ value: STATUS_ARCHIVED, label: 'Archived', count: null });
			}
			return filters;
		},

		totalPages() { return Math.ceil(this.totalCount / this.pageSize) || 1; },
		paginationFrom() { return this.totalCount ? (this.page - 1) * this.pageSize + 1 : 0; },
		paginationTo() { return Math.min(this.page * this.pageSize, this.totalCount); },

		visiblePages() {
			const pages = [];
			for (let i = Math.max(1, this.page - 2); i <= Math.min(this.totalPages, this.page + 2); i++) pages.push(i);
			return pages;
		},
	},

	methods: {
		...mapActions('request', {
			requestRequestList: 'requestList',
		}),

		formatCash,
		formatDate,
		formatDateTime,

		async fetchRequests() {
			let loading = this.$loading.show();
			try {
				const filters = {};
				if (this.searchQuery) filters.search = this.searchQuery;
				if (this.activeStatus !== '---') filters.status = this.activeStatus;

				await this.requestRequestList({
					filters,
					page: this.page,
					pageSize: this.pageSize,
					force: true,
				});

				this.requests = this.$store.state.request.list;
				this.totalCount = this.$store.state.request.totalCount;
			} catch (err) {
				this.$log.error(err);
			} finally {
				loading.hide();
			}
		},

		setStatus(s) {
			this.activeStatus = s;
			this.page = 1;
			this.fetchRequests();
		},

		onSearch() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.page = 1;
				this.fetchRequests();
			}, 300);
		},

		loadPage(p) {
			if (p >= 1 && p <= this.totalPages) {
				this.page = p;
				this.fetchRequests();
			}
		},

		getRequestStatus(req) {
			if (req.status && req.status.length > 0) {
				return req.status[0].status;
			}
			return STATUS_DRAFT;
		},

		statusLabel(req) {
			const s = this.getRequestStatus(req);
			const mapped = STATUS_LABEL_MAP[s];
			return mapped ? mapped.label : '';
		},

		statusBadgeClass(req) {
			const s = this.getRequestStatus(req);
			const mapped = STATUS_LABEL_MAP[s];
			return mapped ? 'v2-req-card__status--' + mapped.modifier : '';
		},

		goToRequest(req) {
			const s = this.getRequestStatus(req);
			if (s === STATUS_APPROVED && req.campaign) {
				this.$router.push({ name: 'v2-campaign-details', params: { id: req.campaign } });
			} else {
				this.$router.push({ name: 'v2-request-details', params: { id: req.id } });
			}
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

.v2-req
	font-family: $v2-font

.v2-req__header
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px

.v2-req__header-left
	display: flex
	align-items: center
	gap: 10px

.v2-req__header-icon
	color: $v2-text-muted

.v2-req__title
	font-size: 22px
	font-weight: 700
	margin: 0
	color: $v2-text

.v2-req__count
	font-size: 12px
	color: $v2-text-muted
	background: #f3f4f6
	padding: 3px 10px
	border-radius: 20px

.v2-req__add-btn
	display: inline-flex
	align-items: center
	gap: 8px
	padding: 9px 20px
	background: $v2-teal
	color: #fff
	border: none
	border-radius: 10px
	font-family: $v2-font
	font-size: 13px
	font-weight: 600
	text-decoration: none
	transition: all 0.15s

	&:hover
		background: darken($v2-teal, 8%)

.v2-req__filters
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 20px
	gap: 16px
	flex-wrap: wrap

.v2-req__pills
	display: flex
	gap: 6px

.v2-req__pill
	display: inline-flex
	align-items: center
	gap: 4px
	padding: 6px 14px
	border: 1.5px solid $v2-border
	border-radius: 20px
	background: transparent
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: $v2-teal
		color: $v2-teal

	&--active
		background: rgba($v2-teal, 0.08)
		border-color: $v2-teal
		color: $v2-teal
		font-weight: 600

.v2-req__pill-count
	font-size: 11px
	opacity: 0.7

.v2-req__filter-right
	display: flex
	align-items: center
	gap: 10px

.v2-req__search
	display: flex
	align-items: center
	gap: 8px
	padding: 7px 14px
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: 10px
	min-width: 220px
	color: $v2-text-muted

.v2-req__search-input
	border: none !important
	outline: none !important
	background: transparent !important
	font-family: $v2-font
	font-size: 13px
	color: $v2-text
	width: 100%
	padding: 0 !important
	min-height: 0 !important
	box-shadow: none !important

	&::placeholder
		color: $v2-text-label

.v2-req__view-toggle
	display: flex
	background: #f3f4f6
	border-radius: 8px
	padding: 2px

.v2-req__view-btn
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	border: none
	background: transparent
	border-radius: 6px
	cursor: pointer
	color: $v2-text-muted
	transition: all 0.15s

	&:hover
		color: $v2-text

	&--active
		background: $v2-card-bg
		color: $v2-text
		box-shadow: 0 1px 3px rgba(0,0,0,0.08)

.v2-req__cards
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px

	@media (max-width: 1024px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2-req-card
	background: $v2-card-bg
	border: 1px solid $v2-border
	border-radius: $v2-radius-card
	padding: 18px 20px
	cursor: pointer
	transition: all 0.2s
	display: flex
	flex-direction: column

	&:hover
		box-shadow: $v2-shadow-hover
		transform: translateY(-2px)

.v2-req-card__top
	display: flex
	align-items: flex-start
	justify-content: space-between
	gap: 10px
	margin-bottom: 14px

.v2-req-card__name-block
	flex: 1
	min-width: 0

.v2-req-card__name
	font-size: 14px
	font-weight: 600
	color: $v2-text
	display: block

.v2-req-card__status
	font-size: 11px
	font-weight: 600
	padding: 3px 10px
	border-radius: 20px
	white-space: nowrap
	flex-shrink: 0

	&--green
		background: rgba($v2-green, 0.1)
		color: darken($v2-green, 10%)

	&--yellow
		background: rgba(#eab308, 0.1)
		color: #a16207

	&--blue
		background: rgba(#3b82f6, 0.1)
		color: #1d4ed8

	&--red
		background: rgba(#ef4444, 0.1)
		color: #b91c1c

	&--gray
		background: rgba(#9ca3af, 0.12)
		color: #6b7280

.v2-req-card__info
	display: flex
	flex-direction: column
	gap: 6px
	margin-bottom: 14px
	flex: 1

.v2-req-card__row
	display: flex
	align-items: center
	gap: 8px
	font-size: 12px
	color: $v2-text-muted

	svg
		flex-shrink: 0
		color: $v2-text-label

.v2-req-card__footer
	display: flex
	justify-content: space-between
	align-items: center
	padding-top: 12px
	border-top: 1px solid $v2-border

.v2-req-card__agency
	font-size: 11px
	color: $v2-text-muted

.v2-req-card__date
	font-size: 11px
	color: $v2-text-label

.v2-req__table-wrap
	overflow-x: auto

.v2-req__row
	cursor: pointer

.v2-req__row-name
	font-weight: 600

.v2-req__empty-cell
	text-align: center
	padding: 40px !important
	color: $v2-text-muted

.v2-req__empty
	grid-column: 1 / -1
	text-align: center
	padding: 60px 20px
	color: $v2-text-muted

.v2-req__empty-sub
	font-size: 13px
	color: $v2-text-label

.v2-req__pagination
	display: flex
	align-items: center
	justify-content: space-between
	margin-top: 20px

.v2-req__pagination-info
	font-size: 13px
	color: $v2-text-muted

.v2-req__pagination-right
	display: flex
	align-items: center
	gap: 12px

.v2-req__pagination-label
	font-size: 12px
	color: $v2-text-muted

.v2-req__pagination-select
	padding: 5px 8px
	border: 1px solid $v2-border
	border-radius: 8px
	font-family: $v2-font
	font-size: 12px
	background: $v2-card-bg
	color: $v2-text

.v2-req__pagination-pages
	display: flex
	gap: 4px

.v2-req__page-btn
	width: 32px
	height: 32px
	display: flex
	align-items: center
	justify-content: center
	border: 1px solid $v2-border
	border-radius: 8px
	background: $v2-card-bg
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover:not(:disabled)
		border-color: $v2-teal
		color: $v2-teal

	&:disabled
		opacity: 0.4
		cursor: default

	&--active
		background: $v2-teal
		border-color: $v2-teal
		color: #fff
</style>
